import type { languages } from "monaco-editor";

export const LIQUID_THEME = "liquidTheme";

/** `monaco.languages.IndentAction` values, so the language configs load without Monaco itself. */
export const indentAction = {
  indent: 1,
  indentOutdent: 2,
} as const satisfies Record<string, languages.IndentAction>;

export const brazeLiquidTags = [
  "if",
  "else",
  "elsif",
  "endif",
  "render",
  "assign",
  "capture",
  "endcapture",
  "case",
  "endcase",
  "comment",
  "endcomment",
  "cycle",
  "decrement",
  "for",
  "endfor",
  "include",
  "increment",
  "layout",
  "raw",
  "endraw",
  "render",
  "tablerow",
  "endtablerow",
  "unless",
  "endunless",
  "translation",
  "endtranslation",
  "message_component",
];

export const extraBrazeFilters = [
  // Extra Braze filters: https://www.braze.com/docs/user_guide/personalization_and_dynamic_content/liquid/filters/
  "money",
  "money_without_trailing_zeros",
  "md5",
  "sha1",
  "hmac_sha1",
  "hmac_sha256",
  "time_zone",
  // Advanced filters: https://www.braze.com/docs/user_guide/personalization_and_dynamic_content/liquid/advanced_filters/#encoding-filters
  "sha2",
  "base64",
  "hmac_sha1_hex",
  "hmac_sha1_base64",
  "hmac_sha256_hex",
  "hmac_sha256_base64",
  "hmac_sha512_hex",
  "hmac_sha512_base64",
  "url_escape",
  "url_param_escape",
  "url_encode",
  "property_accessor",
  "number_with_delimiter",
  "json_escape",
];

export const brazeLiquidFilters = [
  "abs",
  "append",
  "at_least",
  "at_most",
  "capitalize",
  "ceil",
  "compact",
  "date",
  "default",
  "divided_by",
  "downcase",
  "escape",
  "escape_once",
  "first",
  "floor",
  "join",
  "json",
  "last",
  "lstrip",
  "map",
  "minus",
  "modulo",
  "newline_to_br",
  "plus",
  "prepend",
  "remove",
  "remove_first",
  "replace",
  "replace_first",
  "reverse",
  "round",
  "rstrip",
  "size",
  "slice",
  "sort",
  "sort_natural",
  "split",
  "strip",
  "strip_html",
  "strip_newlines",
  "times",
  "truncate",
  "truncatewords",
  "uniq",
  "upcase",
  "url_decode",
  "url_encode",
  "where",
  ...extraBrazeFilters,
];

export const liquidSnippets = [
  { label: "assign", insertText: "{% assign ${1:variable_name} = ${2:value} %}" },
  { label: "capture", insertText: "{% capture ${1:variable_name} %}${2:text}{% endcapture %}" },
  { label: "if", insertText: "{% if ${1:condition} %}\n\t${2:/* code */}\n{% endif %}" },
  { label: "elsif", insertText: "{% elsif ${1:condition} %}" },
  { label: "else", insertText: "{% else %}" },
  { label: "for", insertText: "{% for ${1:item} in ${2:collection} %}\n\t{{ ${3:/* code */} }}\n{% endfor %}" },
  { label: "unless", insertText: "{% unless ${1:condition} %}\n\t${2:/* code */}\n{% endunless %}" },
  { label: "break", insertText: "{% break %}" },
  { label: "continue", insertText: "{% continue %}" },
  { label: "connected_content", insertText: "{% connected_content %}" },
  { label: "abort_message", insertText: "{% abort_message %}" },
  { label: "catalog_items", insertText: "{% catalog_items %}" },
  { label: "catalog_selection_items", insertText: "{% catalog_selection_items %}" },
  { label: "message_extras", insertText: "{% message_extras :key ${1:key} :value ${2:value} %}" },
  { label: "translation", insertText: "{% translation ${1:id} %}${2:/* text */}{% endtranslation %}" },
  {
    label: "invoke_agent",
    insertText:
      '{% invoke_agent "${1:agent_name}" :save ${2:agent_output} %}\n${3:/* text */}\n{% endinvoke_agent %}\n{{ ${2:agent_output} }}',
  },
];

export const customizedLiquidTokenizer = {
  liquidNestedState: [
    [/\{\{/, "delimiter.output.liquid", "@push"],
    [/\}\}/, { token: "delimiter.output.liquid" }, "@pop"],
    [/\{%/, "delimiter.tag.liquid", "@push"],
    [/raw\s*%\}/, "delimiter.tag.liquid", "@liquidRaw"],
    [/%\}/, { token: "delimiter.tag.liquid" }, "@pop"],
    [/(\$\{)([^}]*)(\})/, ["braze.liquid.attribute", "braze.liquid.attribute", "braze.liquid.attribute"]],
    { include: "liquidRoot" },
  ],
  liquidRaw: [
    [/^(?!\{%\s*endraw\s*%\}).+/],
    [/\{%/, "delimiter.tag.liquid"],
    [/@identifier/],
    [/%\}/, { token: "delimiter.tag.liquid", next: "@root" }],
  ],
  liquidCatalogItems: [[/\S+/, "braze.liquid.attribute", "@pop"]],
  liquidRoot: [
    [
      /(catalog_items|catalog_selection_items)(\s*?)(%\})/,
      ["braze.liquid.namespace", "", "delimiter.tag.liquid"],
      "@pop",
    ],
    [
      /(catalog_items|catalog_selection_items)(\s*?)(\S+)(\s*?)(%\})/,
      ["braze.liquid.namespace", "", "braze.liquid.attribute", "", "delimiter.tag.liquid"],
      "@pop",
    ],
    [/catalog_items|catalog_selection_items/, "braze.liquid.namespace", "@liquidCatalogItems"],
    [/\d+(\.\d+)?/, "number.liquid"],
    [/"[^"]*"/, "string.liquid"],
    [/'[^']*'/, "string.liquid"],
    [/\s+/],
    [
      /@symbol/,
      {
        cases: {
          "@operators": "operator.liquid",
          "@default": "",
        },
      },
    ],
    [/\./],
    [
      /@identifier/,
      {
        cases: {
          "@constants": "keyword.liquid",
          "@builtinFilters": "predefined.liquid",
          "@builtinTags": "predefined.liquid",
          "@namespaces": "braze.liquid.namespace",
          "@abortMessage": "braze.liquid.abortMessage",
          "@connectedContent": "braze.liquid.connectedContent",
          "@default": "variable.liquid",
        },
      },
    ],
    [/[^:}|%]/, "variable.liquid"],
  ],
};
